import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { MonitorSmartphone } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { useRelativeTime } from '@hooks/useRelativeTime';
import type { UserSession } from '../../types/profile';
import SessionDeviceCell from './SessionDeviceCell';

interface SessionsTableProps {
  sessions: UserSession[];
  loading?: boolean;
  onRevoke: (id: string) => void;
}

const SessionsTable: React.FC<SessionsTableProps> = ({ sessions, loading, onRevoke }) => {
  const { t } = useTranslation();
  const relativeTime = useRelativeTime();

  const columns = useMemo<ColumnDef<UserSession, any>[]>(
    () => [
      {
        id: 'device',
        header: t('settings.security.device'),
        cell: ({ row }) => <SessionDeviceCell userAgent={row.original.user_agent} />,
      },
      {
        // The API exposes no geolocation, so this is the address itself rather
        // than an invented city.
        id: 'address',
        header: t('settings.security.ipAddress'),
        cell: ({ row }) => (
          <span className="revy-figure text-xs text-muted-foreground">
            {row.original.ip_address ?? '—'}
          </span>
        ),
      },
      {
        id: 'lastActive',
        header: t('settings.security.lastActive'),
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {relativeTime(row.original.last_activity)}
          </span>
        ),
      },
      {
        id: 'status',
        header: t('settings.security.status'),
        cell: ({ row }) =>
          row.original.is_current ? (
            <Badge variant="secondary" className="border-success/30 bg-success/10 text-success">
              {t('common.currentSession')}
            </Badge>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="h-8 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => onRevoke(row.original.id)}
            >
              {t('settings.security.signOut')}
            </Button>
          ),
      },
    ],
    [t, relativeTime, onRevoke],
  );

  return (
    <DataTable
      paginated
      columns={columns}
      data={sessions}
      loading={loading}
      getRowId={(row) => row.id}
      loadingLabel={t('common.loading')}
      empty={
        <div className="flex flex-col items-center gap-2 py-6">
          <MonitorSmartphone className="size-6 text-muted-foreground" strokeWidth={1.6} />
          <p className="text-sm text-muted-foreground">{t('profile.noActiveSessions')}</p>
        </div>
      }
    />
  );
};

export default SessionsTable;
